import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { updateStaffMember, uploadStaffPhoto, StaffMember } from "@/services/staffService";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatPhoneNumber } from "@/lib/format";
import BasicInfoFields from "./form/BasicInfoFields";
import RoleField from "./form/RoleField";
import StatusField from "./form/StatusField";
import LocationField from "./form/LocationField";
import StartDateField from "./form/StartDateField";
import BirthdayPicker from "./form/BirthdayPicker";
import PhotoUpload from "./form/PhotoUpload";

// Define the profile form schema
const staffProfileSchema = z.object({
  first_name: z.string().min(1, "First name is required"),
  last_name: z.string().min(1, "Last name is required"),
  email: z.union([
    z.string().email("Invalid email address"),
    z.string().length(0).transform(() => null),
    z.null()
  ]).optional(),
  phone: z.string().nullable().optional(),
  role: z.enum([
    "instructor",
    "admin",
    "receptionist",
    "manager",
    "owner",
    "front_desk",
    "other"
  ]),
  is_active: z.boolean().default(true),
  profile_photo_url: z.string().nullable().optional(),
  location_id: z.string().nullable().optional(),
  start_date: z.string().nullable().optional(),
  birthday: z.string().nullable().optional(),
  birthday_month: z.number().nullable().optional(),
  birthday_day: z.number().nullable().optional(),
});

export type StaffProfileFormValues = z.infer<typeof staffProfileSchema>;

interface StaffProfileFormProps {
  staffMember: StaffMember;
  onSaved: () => void;
  onCancel: () => void;
}

const StaffProfileForm: React.FC<StaffProfileFormProps> = ({ 
  staffMember, 
  onSaved, 
  onCancel 
}) => {
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(
    staffMember?.profile_photo_url || null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<StaffProfileFormValues>({
    resolver: zodResolver(staffProfileSchema),
    defaultValues: {
      first_name: staffMember?.first_name || "",
      last_name: staffMember?.last_name || "",
      email: staffMember?.email || "",
      phone: staffMember?.phone || "",
      role: (staffMember?.role as any) || "instructor",
      is_active: staffMember?.is_active !== undefined ? staffMember.is_active : true,
      profile_photo_url: staffMember?.profile_photo_url || null,
      location_id: staffMember?.location_id || null,
      start_date: staffMember?.start_date || null,
      birthday: staffMember?.birthday || null,
      birthday_month: staffMember?.birthday_month || null,
      birthday_day: staffMember?.birthday_day || null,
    },
  });

  const getInitials = (firstName: string, lastName: string) => {
    if (!firstName && !lastName) return "?";
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setPhotoFile(file);
      
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          setPhotoPreview(event.target.result as string);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const onSubmit = async (values: StaffProfileFormValues) => {
    try {
      setIsSubmitting(true);

      // Ensure phone is properly formatted
      const formattedPhone = values.phone ? formatPhoneNumber(values.phone) : null;
      
      const updatedData = {
        first_name: values.first_name,
        last_name: values.last_name,
        email: values.email || null,
        phone: formattedPhone,
        role: values.role,
        is_active: values.is_active,
        start_date: values.start_date,
        location_id: values.location_id,
        birthday: values.birthday,
        birthday_month: values.birthday_month,
        birthday_day: values.birthday_day,
      };

      // Update staff profile
      await updateStaffMember(staffMember.id, updatedData);
      
      // Handle photo upload if a new photo was selected
      if (photoFile) {
        try {
          const photoUrl = await uploadStaffPhoto(photoFile, staffMember.id);
          
          if (photoUrl) {
            // Update the staff record with the new photo URL
            await updateStaffMember(staffMember.id, {
              profile_photo_url: photoUrl
            });
          }
        } catch (error) {
          console.error("Error uploading staff photo:", error);
          // Don't fail the whole operation if photo upload fails
        }
      }
      
      toast.success("Staff profile updated successfully");
      onSaved();
    } catch (error) {
      console.error("Error updating staff profile:", error);
      toast.error("Failed to update staff profile", {
        description: error instanceof Error ? error.message : "Unknown error"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <PhotoUpload 
          photoPreview={photoPreview}
          getInitials={getInitials}
          handleFileChange={handleFileChange}
          firstName={form.watch('first_name')}
          lastName={form.watch('last_name')}
          hasExistingPhoto={!!staffMember?.profile_photo_url}
        />

        <BasicInfoFields form={form} />
        
        <div className="grid grid-cols-2 gap-4">
          <StartDateField form={form} />
          <LocationField form={form} />
        </div>
        
        <BirthdayPicker form={form} />
        
        <RoleField form={form} />
        <StatusField form={form} />
        
        <div className="flex justify-end space-x-2 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default StaffProfileForm; 